import apiClient from '../api';
import { ContestSeason, Phase, SeasonStatus } from '../../types';

export interface CreateSeasonRequest {
  name: string;
  startDate: string;
  endDate?: string;
  primaryShowId?: string;
}

export interface UpdateSeasonRequest {
  name: string;
  startDate: string;
  endDate?: string;
  primaryShowId?: string;
  /** Optional status change. Values: Planned | Active | Completed | Archived */
  status?: SeasonStatus;
}

export const seasonService = {
  /** List all non-archived seasons for the current user (or a viewed athlete when viewAs is provided). */
  getSeasons: async (viewAs?: string): Promise<ContestSeason[]> => {
    const params = viewAs ? { viewAs } : {};
    const response = await apiClient.get('/seasons', { params });
    return response.data;
  },

  /** Get a single season by ID. */
  getSeason: async (id: string): Promise<ContestSeason> => {
    const response = await apiClient.get(`/seasons/${id}`);
    return response.data;
  },

  /** Create a new season. Starts with status = Planned. */
  createSeason: async (req: CreateSeasonRequest): Promise<ContestSeason> => {
    const response = await apiClient.post('/seasons', req);
    return response.data;
  },

  /** Update an existing season (name, dates, primaryShowId, status). */
  updateSeason: async (id: string, req: UpdateSeasonRequest): Promise<ContestSeason> => {
    const response = await apiClient.put(`/seasons/${id}`, req);
    return response.data;
  },

  /**
   * Soft-delete a season — sets Status = Archived on the backend.
   * Does not delete associated shows.
   */
  archiveSeason: async (id: string): Promise<void> => {
    await apiClient.delete(`/seasons/${id}`);
  },

  /** Get all phases belonging to a season, ordered by start date. Supports coach viewAs. */
  getSeasonPhases: async (seasonId: string, viewAs?: string): Promise<Phase[]> => {
    const params = viewAs ? { viewAs } : {};
    const response = await apiClient.get(`/seasons/${seasonId}/phases`, { params });
    return response.data;
  },

  /** Create a new season-level phase (not tied to a specific show). */
  createSeasonPhase: async (seasonId: string, req: PhaseRequest): Promise<Phase> => {
    const response = await apiClient.post(`/seasons/${seasonId}/phases`, req);
    return response.data;
  },

  /** Update an existing phase (name, type, dates). */
  updatePhase: async (phaseId: string, req: PhaseRequest): Promise<Phase> => {
    const response = await apiClient.put(`/phases/${phaseId}`, req);
    return response.data;
  },

  /** Delete a phase. Cycles referencing it will have their phaseId cleared. */
  deletePhase: async (phaseId: string): Promise<void> => {
    await apiClient.delete(`/phases/${phaseId}`);
  },
};

export interface PhaseRequest {
  name: string;
  phaseType: string;
  startDate: string;
  endDate: string;
}
